if (!customElements.get('landing-anchor-nav')) {
  // Overview cards plus a sticky bar for long landing pages.
  // - The bar appears once the cards scroll away and hides after the last linked section.
  // - It sits below the site header while the header is showing.
  // - It marks the section being read (aria-current) and fills a progress line.
  // - Links scroll to their section with room for the bar, and move focus to its heading.
  class LandingAnchorNav extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-anchor-bar]');
      this.scroller = this.querySelector('[data-anchor-scroller]');
      this.scrollerWrap = this.querySelector('[data-anchor-scroller-wrap]');
      this.header = document.querySelector('.section-header');
      this.stickyHeader = document.querySelector('sticky-header');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onLinkClick = this.onLinkClick.bind(this);
      this.links = Array.from(this.querySelectorAll('a[data-anchor-link]'));
      this.links.forEach((link) => link.addEventListener('click', this.onLinkClick));

      if (!this.bar) return;

      // Bar links in page order, each paired with the section it points to.
      this.items = Array.from(this.bar.querySelectorAll('.landing-anchor-bar__link'))
        .map((link) => ({ link, target: this.targetOf(link) }))
        .filter((item) => item.target);
      if (this.items.length === 0) return;

      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      };
      this.onScrollerScroll = () => this.updateFades();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.scroller.addEventListener('scroll', this.onScrollerScroll, { passive: true });

      if (this.header && 'MutationObserver' in window) {
        this.headerObserver = new MutationObserver(() => {
          this.updateHeaderOffset();
          this.onScroll();
        });
        this.headerObserver.observe(this.header, { attributes: true, attributeFilter: ['class'] });
      }

      this.updateHeaderOffset();
      this.update();
      this.updateFades();
    }

    disconnectedCallback() {
      this.links?.forEach((link) => link.removeEventListener('click', this.onLinkClick));
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      if (this.onScrollerScroll) this.scroller?.removeEventListener('scroll', this.onScrollerScroll);
      this.headerObserver?.disconnect();
    }

    targetOf(link) {
      const href = link.getAttribute('href') || '';
      if (!href.startsWith('#') || href.length < 2) return null;
      try {
        return document.getElementById(decodeURIComponent(href.slice(1)));
      } catch (error) {
        return null;
      }
    }

    headerAlwaysSticky() {
      const type = this.stickyHeader?.getAttribute('data-sticky-type');
      return type === 'always' || type === 'reduce-logo-size';
    }

    // True while the sticky site header is showing over the page.
    headerIsShowing() {
      if (!this.header) return false;
      return (
        this.header.classList.contains('shopify-section-header-sticky') &&
        !this.header.classList.contains('shopify-section-header-hidden')
      );
    }

    updateHeaderOffset() {
      this.bar.classList.toggle('is-below-header', this.headerIsShowing());
    }

    update() {
      const barTop = this.headerIsShowing() ? this.header.offsetHeight : 0;
      const barBottom = barTop + this.bar.offsetHeight;
      // A section counts as current once its top is within 24px of the bar. Jumps land it 16px below.
      const readingLine = barBottom + 24;
      const first = this.items[0].target.getBoundingClientRect();
      const last = this.items[this.items.length - 1].target.getBoundingClientRect();

      // Show the bar once the overview has scrolled away or the first linked section has reached
      // the reading line (a jump to the first section leaves the overview's last few pixels on screen).
      const started = this.getBoundingClientRect().bottom < barBottom || first.top <= readingLine;
      const beforeEnd = last.bottom > readingLine + 48;
      this.setVisible(started && beforeEnd);

      let active = null;
      this.items.forEach((item) => {
        if (item.target.getBoundingClientRect().top <= readingLine) active = item;
      });
      if (active !== this.active) this.setActive(active);

      const span = Math.max(1, last.bottom - first.top);
      const progress = Math.min(1, Math.max(0, (readingLine - first.top) / span));
      this.bar.style.setProperty('--anchor-progress', progress.toFixed(4));
    }

    setVisible(visible) {
      if (visible === this.visible) return;
      this.visible = visible;
      this.bar.classList.toggle('is-visible', visible);
      if (visible) this.updateFades();
    }

    setActive(active) {
      this.active = active;
      this.items.forEach((item) => {
        if (item === active) {
          item.link.setAttribute('aria-current', 'true');
        } else {
          item.link.removeAttribute('aria-current');
        }
      });
      if (active) this.revealInScroller(active.link);
    }

    // On narrow screens the list scrolls sideways; keep the current link centred in view.
    revealInScroller(link) {
      const { scroller } = this;
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;
      const left = link.offsetLeft - (scroller.clientWidth - link.offsetWidth) / 2;
      scroller.scrollTo({ left: Math.max(0, left), behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }

    updateFades() {
      if (!this.scroller || !this.scrollerWrap) return;
      const max = this.scroller.scrollWidth - this.scroller.clientWidth;
      this.scrollerWrap.classList.toggle('can-scroll-start', this.scroller.scrollLeft > 2);
      this.scrollerWrap.classList.toggle('can-scroll-end', this.scroller.scrollLeft < max - 2);
    }

    onLinkClick(event) {
      const link = event.currentTarget;
      const target = this.targetOf(link);
      if (!target) return;
      event.preventDefault();

      // After jumping down the header hides, so only the bar needs room. Jumping up would
      // reveal the header, so ask it to stay hidden (Dawn's sticky header listens for this).
      const headerRoom = this.headerAlwaysSticky() && this.header ? this.header.offsetHeight : 0;
      const barRoom = target === this || !this.bar ? 0 : this.bar.offsetHeight + 16;
      const top = target.getBoundingClientRect().top + window.scrollY - headerRoom - barRoom;
      if (top < window.scrollY) this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));

      const destination = Math.max(0, top);
      if (this.reducedMotion.matches) {
        window.scrollTo({ top: destination, behavior: 'instant' });
      } else {
        // Long smooth scrolls take seconds, so cover most of a long distance instantly first.
        const distance = destination - window.scrollY;
        const glide = window.innerHeight * 1.5;
        if (Math.abs(distance) > glide * 2) {
          window.scrollTo({ top: destination - Math.sign(distance) * glide, behavior: 'instant' });
        }
        window.scrollTo({ top: destination, behavior: 'smooth' });
      }
      if (target.id && window.location.hash !== `#${target.id}`) {
        window.history.pushState(null, '', `#${target.id}`);
      }
      this.focusSection(target);
    }

    // Move focus to the section heading so keyboard and screen reader users continue from there.
    focusSection(target) {
      const heading = target.querySelector('h2') || target;
      if (!heading.hasAttribute('tabindex')) {
        heading.setAttribute('tabindex', '-1');
        heading.setAttribute('data-anchor-focus', '');
        heading.addEventListener(
          'blur',
          () => {
            heading.removeAttribute('tabindex');
            heading.removeAttribute('data-anchor-focus');
          },
          { once: true }
        );
      }
      heading.focus({ preventScroll: true });
    }
  }

  customElements.define('landing-anchor-nav', LandingAnchorNav);
}
